import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getWebinarStatus } from '../api/client';
import ValueProps from './Marketing/ValueProps';
import SocialProof from './Marketing/SocialProof';
import Faq from './Marketing/Faq';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Landing() {
  const token = localStorage.getItem('token');
  const [webinar, setWebinar] = useState(null);

  useEffect(() => {
    getWebinarStatus().then(setWebinar).catch(() => {});
  }, []);

  // Returning, signed-in users skip the marketing page and go to their hub.
  if (token) {
    let user = {};
    try {
      user = JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      user = {};
    }
    const dest = user.role === 'CLIENT' && user.clientId ? '/dashboard' : '/clients';
    return <Navigate to={dest} replace />;
  }

  const dateStr = formatDate(webinar?.date);
  const pct = webinar?.discountPercent || 50;

  return (
    <div className="min-h-screen bg-navy flex flex-col items-center">
      {/* Webinar banner across the very top */}
      {webinar && (
        <div className="w-full bg-navy-light border-b border-gold/20 px-4 py-2.5">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-1.5 text-center">
            <svg className="w-5 h-5 text-gold flex-shrink-0 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-white text-sm font-semibold">
              Free live webinar{dateStr ? ` on ${dateStr}` : ''}
            </span>
            <span className="text-gray-300 text-sm mx-1.5">&middot;</span>
            <span className="text-white text-sm">Attend to get <span className="text-gold font-semibold">{pct}% off</span> a Clarity Map subscription</span>
            <span className="text-gray-300 text-sm mx-1.5">&middot;</span>
            <Link to="/webinar" className="text-gold text-sm font-semibold hover:underline">Register</Link>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="w-full border-b border-white/10">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <img src="/bcd-icon.png" alt="BCD" className="h-7 w-auto" />
            <span className="text-white font-heading font-semibold text-lg">The Clarity Map</span>
          </div>
          <Link to="/login" className="text-gray-200 text-sm font-semibold hover:text-gold">
            Sign in
          </Link>
        </div>
      </div>

      <div className="flex flex-col items-center px-6 py-10 w-full">
        {/* Hero */}
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-white uppercase tracking-wide leading-tight text-center max-w-3xl mb-3">
          Learn exactly what to say in your next high-stakes presentation.
        </h1>
        <p className="text-gold text-lg md:text-xl font-heading font-bold uppercase tracking-wide mb-4">
          No AI required.
        </p>
        <p className="text-gray-300 text-base md:text-lg text-center max-w-lg mb-5 leading-relaxed">
          Turn everything you know into the one thing they remember. Identify your Big Idea,
          build your case, and walk in ready.
        </p>

        <div className="w-full max-w-sm flex flex-col items-center gap-2 mb-2">
          <Link to="/signup" className="btn-gold w-full py-3.5 text-base font-semibold text-center">
            Start your free trial
          </Link>
          <p className="text-gray-400 text-xs">Free 7-day trial. No credit card required.</p>
        </div>

        <p className="text-gray-500 text-sm mb-10">
          $29/year after your trial. Cancel anytime.
        </p>

        {/* Social proof - logos high up, they matter */}
        <SocialProof />

        {/* Why it works */}
        <div className="w-full max-w-lg mb-14">
          <p className="text-sm font-heading font-bold uppercase tracking-widest text-gray-300 text-center mb-8">
            Why it works
          </p>
          <ValueProps />
        </div>

        <Faq />

        {/* Closing CTA */}
        <Link to="/signup" className="btn-gold px-8 py-3.5 text-base font-semibold text-center mb-8">
          Start your free trial
        </Link>

        <p className="text-gray-400 text-sm mb-6">
          Built on Brain-Centric Design methodology
        </p>
      </div>
    </div>
  );
}
